import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, DollarOutlined } from '@ant-design/icons';
import type { BillRow, BillStatus, CreateBillRequest, RequisitionRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Bills (vendor invoices).
 * Real list + add-bill + record-payment. Ties back to a requisition if selected.
 */
const STATUS_TONE: Record<BillStatus, string> = {
  unpaid: 'error', partly_paid: 'warning', paid: 'success', disputed: 'purple',
};

export function BillsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [payFor, setPayFor] = useState<BillRow | null>(null);

  const list = useQuery({
    queryKey: ['bills', institution],
    queryFn: () => productApi.listBills(institution),
    enabled: !!institution,
  });

  const rows = list.data?.rows ?? [];
  const by = list.data?.by_status ?? { unpaid: 0, partly_paid: 0, paid: 0, disputed: 0 };
  const outstanding = list.data?.total_outstanding_paise ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Bills</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Vendor invoices. Partial payments accumulate; audit log records every write.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New bill</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Unpaid" value={by.unpaid} valueStyle={{ color: '#b3382f' }} /></Card>
        <Card size="small"><Statistic title="Partly paid" value={by.partly_paid} valueStyle={{ color: '#a35800' }} /></Card>
        <Card size="small"><Statistic title="Paid" value={by.paid} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Outstanding" value={`₹${paise(outstanding)}`} /></Card>
      </div>

      <Card size="small" title={`${rows.length} bills`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No bills. <a onClick={() => setAddOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<BillRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Vendor', dataIndex: 'vendor_name', key: 'v', render: (v: string) => <b>{v}</b> },
              { title: 'Number', dataIndex: 'number', key: 'n', width: 130, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', width: 120, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Paid', dataIndex: 'paid_paise', key: 'p', width: 120, align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Owed', key: 'o', width: 120, align: 'right', render: (_, r) => r.status === 'paid' ? '—' : <b>₹{paise(r.amount_paise - r.paid_paise)}</b> },
              { title: 'Due', dataIndex: 'due_on', key: 'd', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Status', dataIndex: 'status', key: 's', width: 120, render: (v: BillStatus) => <Tag color={STATUS_TONE[v]}>{v.replace(/_/g, ' ')}</Tag> },
              {
                key: 'action', width: 130, align: 'right',
                render: (_, r) => (
                  r.status !== 'paid' ? (
                    <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => setPayFor(r)}>Pay</Button>
                  ) : null
                ),
              },
            ]}
          />
        )}
      </Card>

      <AddBillModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['bills', institution] });
          void message.success('Bill added');
        }}
      />
      <PayBillModal
        bill={payFor}
        onClose={() => setPayFor(null)}
        institution={institution}
        onDone={() => {
          setPayFor(null);
          void qc.invalidateQueries({ queryKey: ['bills', institution] });
          void message.success('Payment recorded');
        }}
      />
    </Space>
  );
}

function AddBillModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<{ requisition_id?: string; vendor_name: string; number: string; amount_rupees: number; received_on: string; due_on: string; note?: string }>();
  const reqs = useQuery({
    queryKey: ['requisitions', institution],
    queryFn: () => productApi.listRequisitions(institution),
    enabled: !!institution && open,
  });
  const m = useMutation({
    mutationFn: (v: CreateBillRequest) => productApi.createBill(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Modal open={open} title="New bill" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ received_on: today, due_on: today }}
        onFinish={(v) => m.mutate({
          requisition_id: v.requisition_id || undefined,
          vendor_name: v.vendor_name.trim(),
          number: v.number.trim(),
          amount_paise: Math.round(v.amount_rupees * 100),
          received_on: v.received_on,
          due_on: v.due_on,
          note: v.note?.trim() || undefined,
        })}
      >
        <Form.Item name="requisition_id" label="Requisition (optional)">
          <Select
            allowClear
            placeholder="Link to a requisition"
            options={(reqs.data?.rows ?? []).map((r: RequisitionRow) => ({ value: r.id, label: `${r.code} · ${r.subject}` }))}
          />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="vendor_name" label="Vendor" rules={[{ required: true }]}>
            <Input placeholder="Reliable Supplies" autoFocus />
          </Form.Item>
          <Form.Item name="number" label="Invoice #" rules={[{ required: true }]}>
            <Input placeholder="INV-2026-0117" />
          </Form.Item>
          <Form.Item name="amount_rupees" label="Amount (₹)" rules={[{ required: true, type: 'number', min: 0 }]}>
            <InputNumber style={{ width: '100%' }} min={0} step={100} />
          </Form.Item>
          <Form.Item name="received_on" label="Received on" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}>
            <Input placeholder="yyyy-mm-dd" />
          </Form.Item>
          <Form.Item name="due_on" label="Due on" rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}>
            <Input placeholder="yyyy-mm-dd" />
          </Form.Item>
        </div>
        <Form.Item name="note" label="Note">
          <Input placeholder="Line items summary" />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function PayBillModal({
  bill, onClose, institution, onDone,
}: { bill: BillRow | null; onClose: () => void; institution: string; onDone: () => void }) {
  const [form] = Form.useForm<{ amount_rupees: number }>();
  const owed = bill ? bill.amount_paise - bill.paid_paise : 0;
  const m = useMutation({
    mutationFn: (v: { amount_paise: number }) => productApi.payBill(institution, bill!.id, v),
    onSuccess: () => { form.resetFields(); onDone(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal
      open={!!bill}
      title="Record payment"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Record"
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      {bill && (
        <>
          <div style={{ marginBottom: 12, padding: 10, background: '#f7f7f5', borderRadius: 6 }}>
            <div style={{ fontWeight: 600 }}>{bill.vendor_name} · {bill.number}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
              Owed <b>₹{paise(owed)}</b> · due {bill.due_on}
            </Typography.Text>
          </div>
          <Form form={form} layout="vertical" initialValues={{ amount_rupees: owed / 100 }}
            onFinish={(v) => m.mutate({ amount_paise: Math.round(v.amount_rupees * 100) })}>
            <Form.Item name="amount_rupees" label="Amount (₹)" rules={[{ required: true, type: 'number', min: 0.01, max: owed / 100 }]}>
              <InputNumber style={{ width: '100%' }} min={0.01} max={owed / 100} step={100} />
            </Form.Item>
            {err && <Alert type="error" showIcon message={err} />}
          </Form>
        </>
      )}
    </Modal>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
